import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Gift,
  Network,
  RefreshCw,
  Sparkles,
  Timer,
} from 'lucide-react';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Skeleton,
  Spinner,
} from '../components/ui';
import { api } from '../lib/api';
import { formatNumber, formatPlatform, formatRelative } from '../lib/format';
import { errorMessage, useToast } from '../lib/toast';
import type { SharedIpGroup, SharedIpMember, SharedIpPage } from '../lib/types';
import { usersLink } from '../lib/user-links';
import { IP_LOOKUP, UserDetailDrawer } from './Users';

const PAGE_SIZE = 20;

/**
 * A group registering this fast is worth a second look: one person cycling
 * installs (often for trials) leaves a burst, a household doesn't.
 */
const BURST_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Devices grouped by the IP they last registered from — only IPs with two or
 * more devices, largest first.
 *
 * Fetched when the page opens and not polled: the backend ranks every shared
 * IP off its index and caches that ranking for a couple of minutes, and each
 * page only reads the groups it shows.
 */
export function SharedIpsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [offsets, setOffsets] = useState<number[]>([0]);
  const offset = offsets[offsets.length - 1];
  const [selected, setSelected] = useState<string>();
  const [refreshing, setRefreshing] = useState(false);

  const page = useQuery({
    queryKey: ['shared-ips', offset],
    queryFn: () =>
      api.get<SharedIpPage>('/api/insights/shared-ips', {
        offset,
        limit: PAGE_SIZE,
      }),
    // Matches the backend's cache, so revisiting the page reuses the result.
    staleTime: 2 * 60_000,
    placeholderData: (previous) => previous,
  });

  // Re-ranks once on the server, then drops every cached page so the ones
  // revisited are read from the fresh ranking.
  const refresh = async () => {
    setRefreshing(true);
    try {
      const first = await api.get<SharedIpPage>('/api/insights/shared-ips', {
        offset: 0,
        limit: PAGE_SIZE,
        refresh: true,
      });
      queryClient.removeQueries({ queryKey: ['shared-ips'] });
      queryClient.setQueryData(['shared-ips', 0], first);
      setOffsets([0]);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };

  const data = page.data;

  return (
    <>
      <PageHeader
        title="Shared IPs"
        description="Devices that last registered from the same IP address — households, carrier NATs, or one person cycling installs"
        action={
          <Button
            variant="ghost"
            onClick={() => void refresh()}
            loading={refreshing}
            icon={<RefreshCw size={16} />}
          >
            Refresh
          </Button>
        }
      />

      {page.isError && <ErrorNote error={page.error} />}

      {page.isPending ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-[92px]" />
            ))}
          </div>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-48" />
          ))}
        </div>
      ) : data ? (
        <div className="space-y-4">
          <Summary data={data} />

          {data.items.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Network size={28} />}
                title="No shared IPs"
                description="Every device registered from an address of its own."
              />
            </Card>
          ) : (
            <div className="space-y-3">
              {data.items.map((group) => (
                <GroupCard
                  key={group.ip_address}
                  group={group}
                  onSelect={setSelected}
                />
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={() => setOffsets(offsets.slice(0, -1))}
              disabled={offsets.length <= 1 || page.isFetching}
              icon={<ChevronLeft size={16} />}
            >
              Previous
            </Button>
            {page.isFetching ? (
              <Spinner className="size-4" />
            ) : (
              <span className="text-xs text-ink-3">
                Updated {formatRelative(data.generated_at)}
              </span>
            )}
            <Button
              onClick={() =>
                data.next_offset !== null &&
                setOffsets([...offsets, data.next_offset])
              }
              disabled={data.next_offset === null || page.isFetching}
            >
              Next
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      ) : null}

      <UserDetailDrawer
        deviceId={selected}
        onClose={() => setSelected(undefined)}
        onFilterByIp={(ip) => {
          setSelected(undefined);
          void navigate(usersLink({ ip_address: ip }));
        }}
      />
    </>
  );
}

function Summary({ data }: { data: SharedIpPage }) {
  // Pages are largest-first, so the first group on page one is the largest.
  const largest = data.items[0]?.devices ?? 0;
  const average = data.total_groups
    ? (data.total_devices / data.total_groups).toFixed(1)
    : '0';

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Tile label="Shared IPs" value={formatNumber(data.total_groups)} />
        <Tile
          label="Devices on them"
          value={formatNumber(data.total_devices)}
        />
        <Tile label="Avg devices per IP" value={average} />
        <Tile label="Largest on this page" value={formatNumber(largest)} />
      </div>
      {data.truncated && (
        <p className="text-xs text-ink-3">
          Only the largest groups are listed; the totals above count every
          shared IP.
        </p>
      )}
    </>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-3.5 shadow-sm sm:p-4">
      <p className="text-xs font-medium text-ink-2">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-ink">
        {value}
      </p>
    </div>
  );
}

function GroupCard({
  group,
  onSelect,
}: {
  group: SharedIpGroup;
  onSelect: (deviceId: string) => void;
}) {
  const hidden = group.devices - group.members.length;
  const span = registrationSpan(group);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <a
            href={`${IP_LOOKUP}${encodeURIComponent(group.ip_address)}`}
            target="_blank"
            rel="noreferrer noopener"
            title={`Look up ${group.ip_address}`}
            className="inline-flex items-center gap-1 font-mono text-sm font-semibold break-anywhere text-ink hover:text-brand"
          >
            {group.ip_address}
            <ExternalLink size={13} className="shrink-0 opacity-60" />
          </a>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone="brand">{formatNumber(group.devices)} devices</Badge>
            {group.apps > 1 && <Badge>{group.apps} apps</Badge>}
            {group.countries > 1 && (
              <Badge>{group.countries} countries</Badge>
            )}
            {group.premium > 0 && (
              <Badge tone="good">{formatNumber(group.premium)} premium</Badge>
            )}
            {group.trial > 0 && (
              <Badge
                tone={group.trial > 1 ? 'warn' : 'neutral'}
                icon={<Sparkles size={11} />}
              >
                {formatNumber(group.trial)} on trial
              </Badge>
            )}
            {group.gift > 0 && (
              <Badge tone="brand" icon={<Gift size={11} />}>
                {formatNumber(group.gift)} gifted
              </Badge>
            )}
            {span !== null && group.devices >= 3 && span < BURST_WINDOW_MS && (
              <Badge tone="warn" icon={<Timer size={11} />}>
                All registered within {formatSpan(span)}
              </Badge>
            )}
          </div>
        </div>

        <div className="text-right text-xs text-ink-3">
          <Link
            to={usersLink({ ip_address: group.ip_address })}
            className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
          >
            View in Users
            <ArrowUpRight size={13} />
          </Link>
          <p className="mt-1">Last seen {formatRelative(group.last_seen)}</p>
        </div>
      </div>

      <ul className="divide-y divide-line">
        {group.members.map((member) => (
          <MemberRow
            key={member.device_id}
            member={member}
            onSelect={() => onSelect(member.device_id)}
          />
        ))}
      </ul>

      {hidden > 0 && (
        <Link
          to={usersLink({ ip_address: group.ip_address })}
          className="block border-t border-line px-4 py-2.5 text-xs text-ink-3 transition-colors hover:bg-raised hover:text-ink-2 sm:px-5"
        >
          +{formatNumber(hidden)} more device{hidden === 1 ? '' : 's'} on this
          IP
        </Link>
      )}
    </Card>
  );
}

function MemberRow({
  member,
  onSelect,
}: {
  member: SharedIpMember;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-4 py-2.5 text-left transition-colors hover:bg-raised sm:px-5"
      >
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-ink"
            title={member.device_id}
          >
            {member.device_id}
          </p>
          <p className="truncate text-xs text-ink-3">
            {member.app_label} · {formatPlatform(member.platform)}
            {member.device_model ? ` · ${member.device_model}` : ''}
            {member.country ? ` · ${member.country}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <MemberBadges member={member} />
          <span className="text-xs text-ink-3">
            Registered {formatRelative(member.created_at)} ·{' '}
            {formatNumber(member.session_count)} sessions
          </span>
        </div>
      </button>
    </li>
  );
}

function MemberBadges({ member }: { member: SharedIpMember }) {
  const badges: ReactNode[] = [
    member.is_premium && (
      <Badge key="p" tone="good">
        Premium
      </Badge>
    ),
    member.is_gift && (
      <Badge key="g" tone="brand" icon={<Gift size={11} />}>
        Gift
      </Badge>
    ),
    member.is_trial && (
      <Badge key="t" tone="warn" icon={<Sparkles size={11} />}>
        Trial
      </Badge>
    ),
  ].filter(Boolean);

  return badges.length > 0 ? <>{badges}</> : <Badge>Free</Badge>;
}

/** Milliseconds between the group's first and last registration. */
function registrationSpan(group: SharedIpGroup): number | null {
  if (!group.first_registered_at || !group.last_registered_at) return null;
  return (
    new Date(group.last_registered_at).getTime() -
    new Date(group.first_registered_at).getTime()
  );
}

function formatSpan(ms: number): string {
  const minutes = Math.max(Math.round(ms / 60_000), 1);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.round(minutes / 60)} h`;
}
